import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Upload, Image as ImageIcon, Video, Music, Link as LinkIcon } from 'lucide-react';
import './CreatePromo.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreatePromoModal = ({ isOpen, onClose, onSuccess, user }) => {
  const [step, setStep] = useState(1); // 1: Category, 2: Form
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [genreTags, setGenreTags] = useState([]);
  const [externalLinks, setExternalLinks] = useState([{ type: 'soundcloud', url: '', label: '' }]);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [audio, setAudio] = useState(null);
  const [audioPreview, setAudioPreview] = useState('');
  const [linkedEventId, setLinkedEventId] = useState('');
  const [userEvents, setUserEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const genres = ['house', 'techno', 'drum & bass', 'dubstep', 'trance', 'hip-hop', 'trap', 'bass', 'deep house', 'progressive', 'electro'];
  
  // Different link types based on category (alphabetized, custom always last)
  const getLinkTypes = () => {
    let types = [];
    
    if (category === 'event') {
      types = ['bandcamp', 'beatport', 'mixcloud', 'soundcloud', 'spotify', 'ticket', 'twitch', 'youtube'];
    } else if (category === 'mix_track') {
      types = ['bandcamp', 'beatport', 'mixcloud', 'soundcloud', 'spotify', 'traxsource', 'youtube'];
    } else {
      // general
      types = ['bandcamp', 'beatport', 'mixcloud', 'purchase', 'soundcloud', 'spotify', 'twitch', 'youtube'];
    }
    
    // Always add custom at the end
    return [...types, 'custom'];
  };

  useEffect(() => {
    if (isOpen && category === 'event' && user) {
      fetchUserEvents();
    }
  }, [isOpen, category, user]);

  const fetchUserEvents = async () => {
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/dj-events`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      // Response is an array, not an object
      if (Array.isArray(data)) {
        // Filter to user's events and upcoming only
        const now = new Date();
        const userUpcomingEvents = data
          .filter(event => event.user_id === user.id)
          .filter(event => {
            const eventDate = new Date(event.event_date);
            return eventDate >= now;
          })
          .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
        
        setUserEvents(userUpcomingEvents);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        setError(`Image ${file.name} exceeds 5MB limit`);
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError(`${file.name} is not an image file`);
        return;
      }

      setImages(prev => [...prev, file]);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = '';
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      setError('Video exceeds 50MB limit');
      return;
    }

    if (!file.type.startsWith('video/')) {
      setError('File is not a video');
      return;
    }

    setVideo(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setVideoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeVideo = () => {
    setVideo(null);
    setVideoPreview('');
  };

  const handleAudioSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 30 second limit ~ 1MB for MP3
    if (file.size > 5 * 1024 * 1024) {
      setError('Audio preview exceeds 5MB limit (30 seconds)');
      return;
    }

    if (!file.type.startsWith('audio/')) {
      setError('File is not an audio file');
      return;
    }

    setAudio(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setAudioPreview(reader.result);
    };
    reader.readAsDataURL(file);

    e.target.value = '';
  };

  const removeAudio = () => {
    setAudio(null);
    setAudioPreview('');
  };

  const addExternalLink = () => {
    if (externalLinks.length < 3) {
      setExternalLinks([...externalLinks, { type: 'soundcloud', url: '', label: '' }]);
    }
  };

  const removeExternalLink = (index) => {
    setExternalLinks(prev => prev.filter((_, i) => i !== index));
  };

  const updateExternalLink = (index, field, value) => {
    setExternalLinks(prev => prev.map((link, i) => 
      i === index ? { ...link, [field]: value } : link
    ));
  };

  const toggleGenre = (genre) => {
    setGenreTags(prev => 
      prev.includes(genre) 
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('beatseek_token');

      // Step 1: Create promo
      const promoData = {
        category,
        title: title.trim(),
        description: description.trim(),
        genre_tags: genreTags,
        external_links: externalLinks.filter(link => link.url.trim()),
        linked_event_id: category === 'event' ? linkedEventId : null
      };

      const createResponse = await fetch(`${BACKEND_URL}/api/promos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(promoData)
      });

      const createData = await createResponse.json();
      if (!createData.success) {
        throw new Error(createData.detail || 'Failed to create promo');
      }

      const promoId = createData.promo.id;

      // Step 2: Upload images if any
      if (images.length > 0) {
        const formData = new FormData();
        images.forEach(image => {
          formData.append('files', image);
        });

        await fetch(`${BACKEND_URL}/api/promos/${promoId}/upload-images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      }

      // Step 3: Upload video if any
      if (video) {
        const formData = new FormData();
        formData.append('file', video);

        await fetch(`${BACKEND_URL}/api/promos/${promoId}/upload-video`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      }

      // Step 4: Upload audio if any (for mix_track)
      if (audio) {
        const formData = new FormData();
        formData.append('file', audio);

        await fetch(`${BACKEND_URL}/api/promos/${promoId}/upload-video`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      }

      // Success!
      onSuccess();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setCategory('');
    setTitle('');
    setDescription('');
    setLocation('');
    setEventDate('');
    setGenreTags([]);
    setExternalLinks([{ type: 'soundcloud', url: '', label: '' }]);
    setImages([]);
    setImagePreviews([]);
    setVideo(null);
    setVideoPreview('');
    setAudio(null);
    setAudioPreview('');
    setLinkedEventId('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="create-promo-overlay">
      <div className="create-promo-modal">
        {/* Header */}
        <div className="create-promo-header">
          <h2>
            {step === 1 ? '📢 Create Promo' : `Create ${category === 'mix_track' ? 'Mix/Track' : category === 'event' ? 'Event' : 'Announcement'} Promo`}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="create-promo-content">
          {step === 1 ? (
            // Step 1: Category Selection
            <div className="category-selection">
              <p className="category-instruction">Choose your promo type:</p>
              <div className="category-grid">
                <button
                  className="category-btn mix-track"
                  onClick={() => {
                    setCategory('mix_track');
                    setStep(2);
                  }}
                >
                  <span className="category-icon">🎵</span>
                  <span className="category-name">Mix & Track Release</span>
                  <span className="category-desc">Promote new mixes, tracks, EPs, albums</span>
                </button>

                <button
                  className="category-btn event"
                  onClick={() => {
                    setCategory('event');
                    setStep(2);
                  }}
                >
                  <span className="category-icon">🎉</span>
                  <span className="category-name">Event Promotion</span>
                  <span className="category-desc">Promote gigs, shows, club nights, festivals</span>
                </button>

                <button
                  className="category-btn general"
                  onClick={() => {
                    setCategory('general');
                    setStep(2);
                  }}
                >
                  <span className="category-icon">📢</span>
                  <span className="category-name">General Announcement</span>
                  <span className="category-desc">Collaborations, updates, shoutouts</span>
                </button>
              </div>
            </div>
          ) : (
            // Step 2: Promo Form
            <div className="promo-form">
              {/* Back to Category Selection */}
              <button className="back-to-category-btn" onClick={() => setStep(1)}>
                ← Back to Category Selection
              </button>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {/* Title */}
              <div className="form-group">
                <label>Title (Optional)</label>
                <input
                  type="text"
                  placeholder="Give your promo a catchy title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, 100))}
                  maxLength={100}
                  className="form-input"
                />
                <span className="char-count">{title.length}/100</span>
              </div>

              {/* Description */}
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  placeholder="Tell people about your promo..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                  maxLength={500}
                  rows={4}
                  className="form-textarea"
                />
                <span className="char-count">{description.length}/500</span>
              </div>

              {/* Location - Event only */}
              {category === 'event' && (
                <div className="form-group">
                  <label>Location (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., Fabric London, Brooklyn Warehouse..."
                    value={location}
                    onChange={(e) => setLocation(e.target.value.slice(0, 200))}
                    maxLength={200}
                    className="form-input"
                  />
                  <span className="char-count">{location.length}/200</span>
                </div>
              )}

              {/* Event Date - Event only */}
              {category === 'event' && (
                <div className="form-group">
                  <label>Event Date (Optional)</label>
                  <input
                    type="datetime-local"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    className="form-input"
                  />
                </div>
              )}

              {/* Genre Tags */}
              <div className="form-group">
                <label>Genre Tags (Optional)</label>
                <div className="genre-tags">
                  {genres.map(genre => (
                    <button
                      key={genre}
                      className={`genre-tag ${genreTags.includes(genre) ? 'selected' : ''}`}
                      onClick={() => toggleGenre(genre)}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>

              {/* External Links */}
              <div className="form-group">
                <label>External Links (Optional - Max 3)</label>
                {externalLinks.map((link, index) => (
                  <div key={index} className="link-input-group">
                    <select
                      value={link.type}
                      onChange={(e) => updateExternalLink(index, 'type', e.target.value)}
                      className="link-type-select"
                    >
                      {getLinkTypes().map(type => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="URL"
                      value={link.url}
                      onChange={(e) => updateExternalLink(index, 'url', e.target.value)}
                      className="link-url-input"
                    />
                    <input
                      type="text"
                      placeholder="Label (e.g., 'Listen Here')"
                      value={link.label}
                      onChange={(e) => updateExternalLink(index, 'label', e.target.value)}
                      className="link-label-input"
                    />
                    {externalLinks.length > 1 && (
                      <button
                        className="remove-link-btn"
                        onClick={() => removeExternalLink(index)}
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                ))}
                {externalLinks.length < 3 && (
                  <button className="add-link-btn" onClick={addExternalLink}>
                    <Plus size={18} /> Add Link
                  </button>
                )}
              </div>

              {/* Event Linking (if event category) */}
              {category === 'event' && (
                <div className="form-group">
                  <label>Link to Event Calendar (Optional)</label>
                  <select
                    value={linkedEventId}
                    onChange={(e) => setLinkedEventId(e.target.value)}
                    className="form-select"
                  >
                    <option value="">-- Select an event (optional) --</option>
                    {userEvents.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.event_name} - {new Date(event.event_date).toLocaleDateString()} @ {event.venue_name}
                      </option>
                    ))}
                  </select>
                  {userEvents.length === 0 && (
                    <p className="help-text">No upcoming events found</p>
                  )}
                </div>
              )}

              {/* Image Upload - For all categories */}
              {category !== '' && (
                <div className="form-group">
                  <label>
                    {category === 'mix_track' 
                      ? 'Album/Cover Art' 
                      : category === 'event' 
                      ? 'Event Flyer/Images' 
                      : 'Images/Logo'} (Optional - Max 5, 5MB each)
                  </label>
                  <div className="media-upload-area">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      className="file-input"
                      id="image-upload"
                      disabled={images.length >= 5}
                    />
                    <label htmlFor="image-upload" className={`upload-label ${images.length >= 5 ? 'disabled' : ''}`}>
                      <ImageIcon size={32} />
                      <span>Choose Images</span>
                      <span className="upload-hint">JPG, PNG, GIF - Max 5MB each</span>
                    </label>
                  </div>
                  {imagePreviews.length > 0 && (
                    <div className="image-previews">
                      {imagePreviews.map((preview, index) => (
                        <div key={index} className="image-preview">
                          <img src={preview} alt={`Preview ${index + 1}`} />
                          <button
                            className="remove-preview-btn"
                            onClick={() => removeImage(index)}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Audio Preview Upload - Only for mix_track */}
              {category === 'mix_track' && (
                <div className="form-group">
                  <label>Audio Preview (Optional - Max 30sec)</label>
                  <div className="media-upload-area">
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={handleAudioSelect}
                      className="file-input"
                      id="audio-upload"
                      disabled={!!audio}
                    />
                    <label htmlFor="audio-upload" className={`upload-label ${audio ? 'disabled' : ''}`}>
                      <Music size={32} />
                      <span>Choose Audio Preview</span>
                      <span className="upload-hint">MP3, WAV - Max 30 seconds</span>
                    </label>
                  </div>
                  {audioPreview && (
                    <div className="audio-preview">
                      <audio src={audioPreview} controls />
                      <button
                        className="remove-preview-btn"
                        onClick={removeAudio}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Video Upload - Only for events */}
              {category === 'event' && (
                <div className="form-group">
                  <label>Video (Optional - Max 60sec, 50MB)</label>
                  <div className="media-upload-area">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoSelect}
                      className="file-input"
                      id="video-upload"
                      disabled={!!video}
                    />
                    <label htmlFor="video-upload" className={`upload-label ${video ? 'disabled' : ''}`}>
                      <Video size={32} />
                      <span>Choose Video</span>
                      <span className="upload-hint">MP4, MOV, WebM - Max 50MB</span>
                    </label>
                  </div>
                  {videoPreview && (
                    <div className="video-preview">
                      <video src={videoPreview} controls />
                      <button
                        className="remove-preview-btn"
                        onClick={removeVideo}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Submit */}
              <div className="form-actions">
                <button
                  className="back-btn"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <button
                  className="submit-btn"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Posting...' : 'Post Promo'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatePromoModal;
